import { memo, useCallback, useEffect, useRef, useMemo, useState, use } from 'react';
import { Virtuoso } from 'react-virtuoso';
import type { VirtuosoHandle } from 'react-virtuoso';
import { BibleParagraph } from '../bible/bible-paragraph';
import { useScrollPositionPersistence } from './use-scroll-position-persistence';
import { useVisibleVerse } from './use-visible-verse';
import glenTexture from '../../assets/glen-texture.jpg';
import { FootnoteDataContext } from '@/data/footnotes/footnote-data-context';
import { parseReference, type BibleBook } from '@/lib/utils/bible-utils';
import { Popover, PopoverContent } from '@/components/ui/popover';

interface ReadingViewProps {
    paragraphs: TranslationParagraph[];
    version: string;
    initialScrollIndex: number;
}

// Helper to parse footnote text markers (*i for italic, *r for cross-ref styling)
function parseFootnoteText(text: string): string {
    return text.replace(/\*[ir]/g, '');
}

/**
 * Separate component for each paragraph to localize footnote computation
 * and prevent re-calculating the entire book's footnotes on every scroll.
 */
const ReadingParagraph = memo(({
    paragraph,
    version,
    paragraphIndex,
    registerVerse,
    onFootnoteClick
}: {
    paragraph: TranslationParagraph & { startVerse: number; startOffset: number };
    version: string;
    paragraphIndex: number;
    registerVerse: (element: Element | null, book: BibleBook, chapter: number, verse: number, paragraphIndex: number) => (() => void) | undefined;
    onFootnoteClick: (footnote: TranslationFootnote, element: HTMLElement) => void;
}) => {
    const footnoteContext = use(FootnoteDataContext);

    const footnotesByWord = useMemo(() => {
        if (!footnoteContext || !footnoteContext.footnoteMap) return new Map<number, TranslationFootnote>();

        const footnotesByWord = new Map<number, TranslationFootnote>();
        const { book } = parseReference(paragraph.r);
        const words = paragraph.t.split(' ').filter((w: string) => w);

        // Parse paragraph reference to get initial chapter/verse
        const parts = paragraph.r.split(':');
        let activeChapter = parseInt(parts[1]);
        let activeVerse = paragraph.startVerse;

        let wordInVerse = paragraph.startOffset;

        words.forEach((word: string, idx: number) => {
            const features = word.includes('*') ? word.substring(word.indexOf('*') + 1) : '';
            const vPos = features.indexOf('v');
            if (vPos !== -1) {
                const vVal = features.slice(vPos + 1);
                if (vVal.includes(':')) {
                    const [c, v] = vVal.split(':');
                    activeChapter = parseInt(c);
                    activeVerse = parseInt(v);
                } else {
                    activeVerse = parseInt(vVal);
                }
                wordInVerse = 0;
            }

            const verseRef = `csb:${book}:${activeChapter}:${activeVerse}`;
            const verseFootnotes = footnoteContext.getFootnotesForVerse(verseRef);

            // Find footnote for this specific word index in this verse
            const fn = verseFootnotes.find(f => f.w === wordInVerse);
            if (fn) {
                footnotesByWord.set(idx, fn);
            }

            wordInVerse++;
        });

        return footnotesByWord;
    }, [paragraph.r, paragraph.t, paragraph.startVerse, paragraph.startOffset, footnoteContext]);

    return (
        <BibleParagraph
            paragraph={paragraph}
            version={version}
            startVerse={paragraph.startVerse}
            startOffset={paragraph.startOffset}
            paragraphIndex={paragraphIndex}
            registerVerse={registerVerse}
            footnotesByWord={footnotesByWord}
            onFootnoteClick={onFootnoteClick}
        />
    );
});

function ReadingViewComponent({ paragraphs, version, initialScrollIndex }: ReadingViewProps) {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const prevScrollIndexRef = useRef<number>(initialScrollIndex);
    const [activeFootnote, setActiveFootnote] = useState<{ data: TranslationFootnote; anchorEl: HTMLElement } | null>(null);

    // Track visible verse with Intersection Observer
    const { currentVerse, registerVerse } = useVisibleVerse();

    // Persist scroll position based on visible verse
    useScrollPositionPersistence({ currentVerse });

    // Calculate verse offsets across all paragraphs
    const paragraphsWithVerseInfo = useMemo(() => {
        const verseWordCounts: Record<string, number> = {};
        let activeChapter = 0;
        let activeVerse = 0;

        return paragraphs.map(p => {
            const parts = p.r.split(':');
            const refChapter = parseInt(parts[1]);
            const refVerse = parts.length === 5 ? (parseFloat(parts[2]) === 0 ? 1 : Math.ceil(parseFloat(parts[2]))) : 1;

            if (activeChapter === 0 || p.h > 0) {
                activeChapter = refChapter;
                activeVerse = refVerse;
            }

            const startVerse = activeVerse;
            const verseKey = `${activeChapter}:${activeVerse}`;
            const startOffset = verseWordCounts[verseKey] || 0;

            const words = p.t.split(' ').filter(w => w);
            words.forEach(word => {
                const features = word.includes('*') ? word.substring(word.indexOf('*') + 1) : '';
                const vPos = features.indexOf('v');
                if (vPos !== -1) {
                    const vVal = features.slice(vPos + 1);
                    if (vVal.includes(':')) {
                        const [c, v] = vVal.split(':');
                        activeChapter = parseInt(c);
                        activeVerse = parseInt(v);
                    } else {
                        activeVerse = parseInt(vVal);
                    }
                }
                const currentKey = `${activeChapter}:${activeVerse}`;
                verseWordCounts[currentKey] = (verseWordCounts[currentKey] || 0) + 1;
            });

            return { ...p, startVerse, startOffset };
        });
    }, [paragraphs]);

    // Scroll to new index when initialScrollIndex changes
    useEffect(() => {
        if (initialScrollIndex !== prevScrollIndexRef.current) {
            virtuosoRef.current?.scrollToIndex({
                index: initialScrollIndex,
                align: 'start',
                behavior: 'auto'
            });
            prevScrollIndexRef.current = initialScrollIndex;
        }
    }, [initialScrollIndex]);

    const handleFootnoteClick = useCallback((footnote: TranslationFootnote, element: HTMLElement) => {
        setActiveFootnote({ data: footnote, anchorEl: element });
    }, []);

    // Memoize itemContent to prevent unnecessary re-renders
    const itemContent = useCallback((index: number) => {
        const paragraph = paragraphsWithVerseInfo[index];
        return (
            <ReadingParagraph
                key={`paragraph-${paragraph.r}-${index}`}
                paragraph={paragraph}
                version={version}
                paragraphIndex={index}
                registerVerse={registerVerse}
                onFootnoteClick={handleFootnoteClick}
            />
        );
    }, [paragraphsWithVerseInfo, version, registerVerse, handleFootnoteClick]);

    return (
        <div
            className="fixed inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${glenTexture})` }}
        >
            {/* Glassy overlay for legibility */}
            <div className="absolute inset-0 bg-background/80 backdrop-blur-2xl" />

            {/* Full-width scroll container */}
            <div className="relative h-full bible-text">
                <Virtuoso
                    ref={virtuosoRef}
                    className="no-scrollbar h-full w-full"
                    totalCount={paragraphs.length}
                    initialTopMostItemIndex={initialScrollIndex}
                    itemContent={itemContent}
                    increaseViewportBy={1000} // Improve scroll smoothness
                    components={{
                        List: ({ children, ...props }) => (
                            <div {...props} className="max-w-3xl px-6 pt-4">
                                {children}
                            </div>
                        )
                    }}
                    skipAnimationFrameInResizeObserver
                />
            </div>

            {/* Footnote Popover (Global to avoid z-index/clipping issues in Virtuoso) */}
            {activeFootnote && (
                <Popover open={true} onOpenChange={(open) => !open && setActiveFootnote(null)}>
                    <PopoverContent
                        className="max-w-xs p-3 shadow-md border bg-popover text-popover-foreground z-[100]"
                        style={{
                            position: 'fixed',
                            top: activeFootnote.anchorEl.getBoundingClientRect().bottom + 4,
                            left: Math.max(10, Math.min(window.innerWidth - 300, activeFootnote.anchorEl.getBoundingClientRect().left))
                        }}
                    >
                        <div className="text-sm leading-relaxed">
                            {activeFootnote.data.t && (
                                <p className={activeFootnote.data.c ? "mb-2" : ""}>
                                    {parseFootnoteText(activeFootnote.data.t)}
                                </p>
                            )}
                            {activeFootnote.data.c && (
                                <div className="text-xs text-muted-foreground pt-1 border-t border-border/50">
                                    <strong>Cross-refs:</strong> {activeFootnote.data.c}
                                </div>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
}

export const ReadingView = memo(ReadingViewComponent);